import { globalFormatAmount } from "./globalFormatAmount";
import { isNoDecimalCurrency } from "./no-decimal-currencies";

/**
 * Montants Flash Pay — conventions du guide §4.3.
 *
 * Tous les montants qui circulent avec l'API sont des ENTIERS en unité
 * mineure, suffixés `Minor` (ex. `amountMinor: 100000` = 100 000 GNF). Le
 * GNF n'a pas de sous-unité en usage : pour lui, 1 unité mineure = 1 GNF.
 * Pour une devise à deux décimales, 100 unités mineures = 1,00 unité.
 *
 * Ces helpers sont le seul endroit où la conversion minor ↔ major a lieu.
 */

/** Devise par défaut de Flash Pay (Guinée). */
export const DEFAULT_CURRENCY = "GNF";

/** Facteur entre unité mineure et unité majeure pour une devise donnée. */
const minorUnitFactor = (currency: string): number =>
  isNoDecimalCurrency(currency) ? 1 : 100;

/**
 * Convertit un montant API (entier, unité mineure) en unité majeure pour
 * l'affichage ou un calcul local. Jamais l'inverse pour un envoi API — voir
 * `majorToMinor`.
 */
export const minorToMajor = (
  amountMinor: number,
  currency: string = DEFAULT_CURRENCY
): number => amountMinor / minorUnitFactor(currency);

/**
 * Convertit une saisie utilisateur (unité majeure) en entier unité mineure
 * prêt à partir vers l'API. L'arrondi est volontaire : l'API refuse les
 * montants non entiers.
 */
export const majorToMinor = (
  amount: number,
  currency: string = DEFAULT_CURRENCY
): number => Math.round(amount * minorUnitFactor(currency));

/**
 * Formate un montant API (unité mineure) pour l'affichage, dans la langue
 * courante de l'app (délègue à `globalFormatAmount`). Devise GNF par défaut.
 *
 * Affichage : toujours envelopper le résultat dans `NoTranslate` (règle
 * notranslate sur les données financières) — ou utiliser directement le
 * composant `AmountMinor` de `features/shared`.
 */
export const formatMinorAmount = (
  amountMinor: number | null | undefined,
  currency: string = DEFAULT_CURRENCY
): string => {
  if (amountMinor == null) return "—";
  return globalFormatAmount(minorToMajor(amountMinor, currency), currency);
};
